<template>
  <teleport to="body">
    <div v-if="open" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ resultado ? 'Factura anulada' : `Anular factura ${numero || ''}` }}
            </h5>
            <button type="button" class="btn-close" aria-label="Cerrar" :disabled="anulando" @click="cerrar"></button>
          </div>

          <div class="modal-body">
            <!-- 1. Cargando la previsualización -->
            <div v-if="cargando" class="text-center py-4">
              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Revisando qué va a pasar…
            </div>

            <div v-else-if="errorCarga" class="alert alert-danger mb-0">{{ errorCarga }}</div>

            <!-- 3. Resultado: la entrega (RN-AN36) -->
            <div v-else-if="resultado">
              <div class="alert alert-success">
                {{ textoResultado }}
              </div>
              <ComprobanteEntrega
                v-if="resultado.entrega?.nota_credito"
                class="mb-3"
                :documento="resultado.entrega.nota_credito"
              />
              <ComprobanteEntrega v-if="resultado.entrega?.factura" :documento="resultado.entrega.factura" />
            </div>

            <!-- 2. Previsualización: qué va a pasar, antes de hacer nada -->
            <div v-else-if="previa">
              <div v-if="previa.bloqueos.length" class="alert alert-warning">
                <div class="fw-semibold mb-1">No se puede anular todavía:</div>
                <ul class="mb-0">
                  <li v-for="b in previa.bloqueos" :key="b">{{ textoBloqueo(b) }}</li>
                </ul>
              </div>
              <div v-else-if="recargandoCaja" class="text-muted small mb-2">Revisando la caja elegida…</div>

              <h6>Ante la SET</h6>
              <p class="mb-3">{{ textoPrevisto }}</p>

              <h6>Mercadería que vuelve al mostrador</h6>
              <ul v-if="previa.mercaderia.length" class="mb-3">
                <li v-for="m in previa.mercaderia" :key="`${m.producto_id}-${m.lote_id}`">
                  {{ m.nombre || `Producto #${m.producto_id}` }} — {{ m.cantidad }} (lote {{ m.lote_id ?? "—" }})
                </li>
              </ul>
              <p v-else class="text-muted mb-3">—</p>

              <template v-if="previa.requiere_caja_abierta">
                <!-- RN-AN20: la propia o una caja abierta de la sucursal -->
                <h6>De qué caja sale el dinero</h6>
                <div v-if="previa.cajas_disponibles?.length" class="mb-1">
                  <select
                    v-model="aperturaElegida"
                    class="form-select form-select-sm"
                    aria-label="Caja de la que sale el dinero"
                    :disabled="anulando || recargandoCaja"
                    @change="cambiarCaja"
                  >
                    <option :value="null" disabled>Elegí una caja abierta</option>
                    <option v-for="c in previa.cajas_disponibles" :key="c.apertura_id" :value="c.apertura_id">
                      {{ textoCaja(c) }}
                    </option>
                  </select>
                </div>
                <p v-else class="text-danger small mb-1">No hay ninguna caja abierta en la sucursal.</p>
                <div v-if="previa.caja && !previa.caja.propia" class="form-text mb-3">
                  El egreso queda en el arqueo de {{ previa.caja.usuario_nombre }}, con vos como autor.
                </div>
                <div v-else class="mb-3"></div>

                <!-- RN-AN19: lo cobrado por defecto; el supervisor puede cambiarlo -->
                <h6>Cómo se devuelve</h6>
                <template v-if="!cambiarMedio">
                  <ul class="mb-1">
                    <li v-for="e in previa.egreso" :key="e.metodo_pago">{{ e.metodo_pago }}: {{ formatearMonto(e.monto) }}</li>
                  </ul>
                  <button type="button" class="btn btn-link btn-sm p-0 mb-2" :disabled="anulando" @click="empezarCambioDeMedio">
                    Devolver por otro medio
                  </button>
                </template>
                <template v-else>
                  <div v-for="(f, i) in formas" :key="i" class="row g-2 mb-2">
                    <div class="col-6">
                      <select v-model="f.metodo_pago" class="form-select form-select-sm" :aria-label="`Medio ${i + 1}`" :disabled="anulando">
                        <option v-for="m in previa.metodos_devolucion" :key="m" :value="m">{{ m }}</option>
                      </select>
                    </div>
                    <div class="col-4">
                      <input v-model.number="f.monto" type="number" min="0" step="1" class="form-control form-control-sm" :aria-label="`Monto ${i + 1}`" :disabled="anulando" />
                    </div>
                    <div class="col-2">
                      <button v-if="formas.length > 1" type="button" class="btn btn-outline-secondary btn-sm w-100" aria-label="Quitar" :disabled="anulando" @click="formas.splice(i, 1)">
                        <i class="bi bi-x"></i>
                      </button>
                    </div>
                  </div>
                  <div class="d-flex gap-3 mb-2">
                    <button type="button" class="btn btn-link btn-sm p-0" :disabled="anulando" @click="formas.push({ metodo_pago: 'EFECTIVO', monto: 0 })">Agregar forma</button>
                    <button type="button" class="btn btn-link btn-sm p-0" :disabled="anulando" @click="cambiarMedio = false">Volver a lo cobrado</button>
                  </div>
                  <template v-if="evaluacion.cambiado">
                    <label for="motivoCambioMedio" class="form-label small fw-semibold mb-1">Por qué se devuelve por otro medio</label>
                    <input id="motivoCambioMedio" v-model="motivoCambioMedio" type="text" maxlength="150" class="form-control form-control-sm mb-1" :disabled="anulando" placeholder="Por ejemplo: el cliente no tiene cuenta bancaria" />
                    <div class="form-text mb-2">Queda registrado en el egreso, junto con lo cobrado.</div>
                  </template>
                </template>
                <div v-if="previa.caja && evaluacion.error" class="alert alert-warning py-2 small mb-3">{{ evaluacion.error }}</div>
                <div v-else class="mb-3"></div>
              </template>
              <p v-else class="text-muted small mb-3">Es una venta a crédito sin pagos: no sale dinero de la caja; el saldo vuelve al cliente.</p>

              <label for="motivoAnulacion" class="form-label fw-semibold">Motivo de la anulación</label>
              <textarea
                id="motivoAnulacion"
                v-model="motivo"
                class="form-control"
                rows="2"
                maxlength="150"
                placeholder="Por ejemplo: el cliente devolvió la mercadería"
                :disabled="!previa.puede_anular || anulando"
              ></textarea>
              <div class="form-text">Entre 5 y 150 caracteres. Queda registrado.</div>

              <div v-if="errorAnular" class="alert alert-danger mt-3 mb-0">{{ errorAnular }}</div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" :disabled="anulando" @click="cerrar">
              {{ resultado ? 'Cerrar' : 'Cancelar' }}
            </button>
            <button
              v-if="!resultado && previa"
              type="button"
              class="btn btn-danger"
              :disabled="!puedeConfirmar"
              @click="confirmar"
            >
              <span v-if="anulando" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Anular factura
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="open" class="modal-backdrop fade show"></div>
  </teleport>
</template>

<script setup>
// Anular una factura desde el histórico de ventas
// (SPEC_ANULACION_FACTURAS_Y_NOTAS_CREDITO_v0.2.md §15). Primero muestra qué va
// a pasar (previsualización, sin escribir nada); después anula y abre la
// entrega (RN-AN36).
import { ref, computed, watch } from 'vue'
import ComprobanteEntrega from '@/components/ComprobanteEntrega.vue'
import {
  previsualizarAnulacion, anularFactura, mensajeDeError, textoInstrumento, textoInstrumentoPrevisto
} from '@/services/anulacionService'
import { evaluarReparto } from '@/utils/repartoDevolucion.mjs'

/* global defineProps, defineEmits */
const props = defineProps({
  open: { type: Boolean, default: false },
  idVenta: { type: Number, default: 0 },
  numero: { type: String, default: '' }
})
const emit = defineEmits(['cerrado', 'anulada'])

const cargando = ref(false)
const anulando = ref(false)
const previa = ref(null)
const resultado = ref(null)
const motivo = ref('')
const errorCarga = ref('')
const errorAnular = ref('')
// La plata (RN-AN19, RN-AN20, RN-AN51 — refinadas 2026-10-11)
const aperturaElegida = ref(null)
const recargandoCaja = ref(false)
const cambiarMedio = ref(false)
const formas = ref([])
const motivoCambioMedio = ref('')

// Textos de los bloqueos de la previsualización (mismos significados que los
// mensajes de error de la API, SPEC §16.4).
const BLOQUEOS = {
  anulacion_ya_anulada: 'Esta venta ya está anulada.',
  anulacion_en_curso: 'Otra persona está anulando esta venta. Probá de nuevo en unos minutos.',
  anulacion_no_anulable: 'Esta venta no se anula desde acá (no es una factura cobrada ni un crédito pendiente).',
  anulacion_credito_con_pagos: 'La venta a crédito ya tiene pagos aplicados: primero hay que resolver esos pagos.',
  anulacion_sin_rastro_de_stock: 'No se encuentra de qué lote salió la mercadería: consultá con soporte.',
  anulacion_sin_caja_abierta: 'No tenés caja abierta: elegí abajo la caja de la que sale el dinero.',
  anulacion_caja_invalida: 'La caja elegida ya no está abierta o es de otra sucursal. Elegí otra.',
  anulacion_otra_sucursal: 'La venta es de otra sucursal: se anula desde la sucursal donde se hizo.',
  anulacion_con_devoluciones: 'La venta ya tiene una devolución con nota de crédito: no se puede anular completa.',
  anulacion_documento_no_resoluble: 'Facturación electrónica todavía no sabe en qué estado está la factura. Probá en unos minutos.',
  anulacion_fe_no_disponible: 'Facturación electrónica no responde. Probá en unos minutos.',
  // P2 (SPEC_VENTAS_INNOMINADAS_FACTURACION_DIFERIDA_v0.1.md RN-VI8, RN-VI9).
  anulacion_ticket_fuera_de_plazo: 'Se cobró con ticket y pasaron más de 24 horas: ya no se anula desde el POS.',
  anulacion_facturada_externa: 'Ya está incluida en una factura del sistema externo: no se anula desde el POS.',
  anulacion_innominada_sin_nc: 'Es una factura a «Cliente sin nombre» fuera del plazo de cancelación: la SET no admite nota de crédito sobre un comprobante innominado.'
}
const textoBloqueo = (b) => BLOQUEOS[b] || b

const textoPrevisto = computed(() => textoInstrumentoPrevisto(previa.value?.fiscal?.instrumento_previsto))
const evaluacion = computed(() => evaluarReparto({
  cobrado: previa.value?.egreso || [],
  elegido: cambiarMedio.value ? formas.value : (previa.value?.egreso || []),
  efectivoDisponible: previa.value?.caja ? previa.value.caja.efectivo_esperado : null
}))
const largoValido = (texto) => texto.trim().length >= 5 && texto.trim().length <= 150
const puedeConfirmar = computed(() => {
  if (!previa.value?.puede_anular || anulando.value || recargandoCaja.value || !largoValido(motivo.value)) return false
  if (!previa.value.requiere_caja_abierta) return true
  return evaluacion.value.valido && (!evaluacion.value.cambiado || largoValido(motivoCambioMedio.value))
})
const textoResultado = computed(() => {
  const a = resultado.value?.anulacion
  const egreso = resultado.value?.egreso
  const caja = egreso?.caja_usuario ? `la caja de ${egreso.caja_usuario}` : 'la caja'
  return `${textoInstrumento(a?.instrumento, a?.numero_nota_credito) || 'Anulada'}. La mercadería volvió al mostrador`
    + (egreso ? ` y el egreso quedó registrado en ${caja}.` : '.')
})
const textoCaja = (c) => `${c.caja_nombre || 'Caja'} — ${c.usuario_nombre}${c.propia ? ' (tu caja)' : ''} · efectivo ${formatearMonto(c.efectivo_esperado)}`

const formatearMonto = (v) => Number(Math.round(Number(v) || 0)).toLocaleString('es-PY')

async function cargar() {
  cargando.value = true
  errorCarga.value = ''
  previa.value = null
  resultado.value = null
  motivo.value = ''
  errorAnular.value = ''
  cambiarMedio.value = false
  formas.value = []
  motivoCambioMedio.value = ''
  try {
    previa.value = await previsualizarAnulacion(props.idVenta)
    aperturaElegida.value = previa.value?.caja?.apertura_id ?? null
  } catch (error) {
    errorCarga.value = mensajeDeError(error, 'No se pudo revisar la factura.')
  } finally {
    cargando.value = false
  }
}

// Al elegir otra caja se vuelve a previsualizar: bloqueos y efectivo cambian.
async function cambiarCaja() {
  recargandoCaja.value = true
  errorAnular.value = ''
  try {
    previa.value = await previsualizarAnulacion(props.idVenta, aperturaElegida.value)
  } catch (error) {
    errorAnular.value = mensajeDeError(error, 'No se pudo revisar la caja elegida.')
  } finally {
    recargandoCaja.value = false
  }
}

function empezarCambioDeMedio() {
  formas.value = (previa.value?.egreso || []).map((e) => ({ metodo_pago: e.metodo_pago, monto: e.monto }))
  cambiarMedio.value = true
}

function opcionesDePlata() {
  if (!previa.value?.requiere_caja_abierta) return {}
  const opciones = {}
  if (previa.value.caja && !previa.value.caja.propia) opciones.apertura_id = previa.value.caja.apertura_id
  if (cambiarMedio.value && evaluacion.value.cambiado) {
    opciones.formas_devolucion = formas.value.map((f) => ({ metodo_pago: f.metodo_pago, monto: Number(f.monto) }))
    opciones.motivo_cambio_medio = motivoCambioMedio.value.trim()
  }
  return opciones
}

async function confirmar() {
  anulando.value = true
  errorAnular.value = ''
  try {
    resultado.value = await anularFactura(props.idVenta, motivo.value.trim(), opcionesDePlata())
    emit('anulada', resultado.value)
  } catch (error) {
    // RN-AN9: si falla, no se anuló nada; el mensaje de la API lo dice.
    errorAnular.value = mensajeDeError(error, 'No se pudo anular la factura. No se anuló nada.')
  } finally {
    anulando.value = false
  }
}

function cerrar() {
  if (anulando.value) return
  emit('cerrado')
}

watch(() => props.open, (abierto) => { if (abierto && props.idVenta) cargar() })
</script>
